import clsx from 'clsx';
import { decodeHtmlWithWhiteList } from 'core/utils/strings';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Button, Dialog, Icon, SelectCustomizable, Tag } from 'ui';
import {
  buildLessonTree,
  getEffectiveSelection,
  isModulesOnlyTocUnit,
  toggleLessonSelection,
  toggleUnitSelection,
  UNAVAILABLE_HELP_ID,
} from '../utils/lessonSelectionTree';

const ARIA_CHECKED_BY_STATE = { checked: true, indeterminate: 'mixed', unchecked: false };

const findCourseById = (courses, courseId) => {
  if (!courseId || !courses?.length) return null;
  return courses.find((c) => c.id === courseId || c.guid === courseId);
};

const LessonSelectionDialog = ({
  t,
  open,
  onClose,
  onSelect,
  courses,
  units,
  loadingUnits,
  loadCourseUnits,
  loadingCourses,
  contentError,
  alreadySelectedLessons = [],
  multiSelect = true,
  defaultCourseId,
}) => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedLessonGuids, setSelectedLessonGuids] = useState(new Set());
  const [expandedUnits, setExpandedUnits] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Single-select state (used when multiSelect=false)
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  // Initialize from already-selected lessons when dialog opens
  useEffect(() => {
    if (open) {
      if (multiSelect) {
        if (alreadySelectedLessons.length > 0) {
          const guids = new Set(alreadySelectedLessons.map((l) => l.guid || l.lessonGuid));
          setSelectedLessonGuids(guids);

          // Auto-select the course from the first already-selected lesson
          const firstLesson = alreadySelectedLessons[0];
          if (firstLesson?.courseGuid && courses?.length > 0) {
            const course = courses.find((c) => c.id === firstLesson.courseGuid);
            if (course) {
              const option = { value: course.id, label: course.title, data: course };
              setSelectedCourse(option);
              loadCourseUnits(course.id);
            }
          }
        } else {
          setSelectedLessonGuids(new Set());
          setExpandedUnits(new Set());
          setSearchQuery('');
          // Preselect default course if available
          const defaultOption =
            defaultCourseId && courses?.length > 0
              ? (() => {
                const c = findCourseById(courses, defaultCourseId);
                return c ? { value: c.id, label: c.title, data: c } : null;
              })()
              : null;
          if (defaultOption) {
            setSelectedCourse(defaultOption);
            loadCourseUnits(defaultOption.value);
          } else {
            setSelectedCourse(null);
          }
        }
      } else {
        // Single-select: reset state
        setSelectedUnit(null);
        setSelectedLesson(null);
        const defaultOption =
          defaultCourseId && courses?.length > 0
            ? (() => {
              const c = findCourseById(courses, defaultCourseId);
              return c ? { value: c.id, label: c.title, data: c } : null;
            })()
            : null;
        if (defaultOption) {
          setSelectedCourse(defaultOption);
          loadCourseUnits(defaultOption.value);
        } else {
          setSelectedCourse(null);
        }
      }
    }
  }, [open]);

  // Handle async race condition: courses may load after dialog opens
  useEffect(() => {
    if (open && !selectedCourse && defaultCourseId && courses?.length > 0) {
      const course = findCourseById(courses, defaultCourseId);
      if (course) {
        const option = { value: course.id, label: course.title, data: course };
        setSelectedCourse(option);
        loadCourseUnits(option.value);
      }
    }
  }, [open, courses, defaultCourseId]);

  const courseOptions = useMemo(() => {
    if (!courses || courses.length === 0) return [];
    return courses.map((item) => ({
      value: item.id,
      label: item.title,
      data: item,
    }));
  }, [courses]);

  // Filter units and lessons by search query
  const filteredUnits = useMemo(() => {
    if (!selectedCourse || !units || units.length === 0) return [];

    const query = searchQuery.toLowerCase().trim();
    if (!query) return units;

    return units
      .map((unit) => {
        const unitTitle = decodeHtmlWithWhiteList(unit.title || '').toLowerCase();
        const unitMatches = unitTitle.includes(query);

        const filteredLessons = (unit.lessons || []).filter((lesson) => {
          const lessonTitle = decodeHtmlWithWhiteList(lesson.title || '').toLowerCase();
          return lessonTitle.includes(query);
        });

        // Include unit if unit name matches OR any lesson matches
        if (unitMatches || filteredLessons.length > 0) {
          return {
            ...unit,
            lessons: unitMatches ? unit.lessons || [] : filteredLessons,
            _autoExpand: filteredLessons.length > 0 && !unitMatches,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [selectedCourse, units, searchQuery]);

  // Auto-expand units that have search matches
  useEffect(() => {
    if (searchQuery.trim()) {
      const toExpand = new Set(expandedUnits);
      filteredUnits.forEach((unit) => {
        if (unit._autoExpand) {
          toExpand.add(unit.id);
        }
      });
      setExpandedUnits(toExpand);
    }
  }, [filteredUnits, searchQuery]);

  const effectiveSelectedIds = useMemo(() => getEffectiveSelection(selectedLessonGuids, units || []), [selectedLessonGuids, units]);

  const lessonTree = useMemo(
    () => buildLessonTree({ selectedIds: effectiveSelectedIds, units: units || [], visibleUnits: filteredUnits }),
    [effectiveSelectedIds, units, filteredUnits]
  );

  const totalSelectedCount = effectiveSelectedIds.size;

  const handleCourseChange = useCallback(
    (option) => {
      setSelectedCourse(option);
      setExpandedUnits(new Set());
      setSearchQuery('');

      if (!multiSelect) {
        setSelectedUnit(null);
        setSelectedLesson(null);
      }

      if (multiSelect) {
        setSelectedLessonGuids(new Set());
      }

      if (option && option.value) {
        loadCourseUnits(option.value);
      }
    },
    [loadCourseUnits, multiSelect]
  );

  // Single-select: unit options
  const unitOptions = useMemo(() => {
    if (!units || units.length === 0) return [];
    return units.map((unit, index) => ({
      value: unit.id,
      label: decodeHtmlWithWhiteList(unit.title || `${t('chatbot:Unit')} ${index + 1}`),
      data: unit,
    }));
  }, [units, t]);

  // Single-select: lesson options from selected unit
  const lessonOptions = useMemo(() => {
    if (!selectedUnit?.data?.lessons) return [];
    return selectedUnit.data.lessons.map((lesson, index) => ({
      value: lesson.id,
      label: decodeHtmlWithWhiteList(lesson.title || `${t('chatbot:Lesson')} ${index + 1}`),
      data: lesson,
    }));
  }, [selectedUnit, t]);

  const hasLessons = useMemo(() => {
    return selectedUnit?.data?.lessons?.length > 0;
  }, [selectedUnit]);

  const handleUnitChange = useCallback((option) => {
    setSelectedUnit(option);
    setSelectedLesson(null);
  }, []);

  const handleLessonChange = useCallback((option) => {
    setSelectedLesson(option);
  }, []);

  const handleSingleConfirm = useCallback(() => {
    if (!hasLessons && selectedCourse && selectedUnit) {
      // For modules-only programs, the unit acts as the lesson
      const unitData = selectedUnit.data;
      const wordCount = unitData.wordCount || (unitData.content ? unitData.content.split(/\s+/).length : 0) || null;

      const selectionData = {
        guid: selectedUnit.value,
        title: selectedUnit.label,
        wordCount: wordCount,
        courseGuid: selectedCourse.value,
        courseName: selectedCourse.label,
        unitName: selectedUnit.label,
        lessonGuid: selectedUnit.value,
        contentIds: unitData.contents?.map((c) => c.id) || [],
        type: 'module',
      };

      onSelect(selectionData);
      return;
    }

    if (!selectedLesson || !selectedCourse) return;

    const lesson = {
      guid: selectedLesson.value,
      title: selectedLesson.label,
      wordCount: null,
      courseGuid: selectedCourse.value,
      courseName: selectedCourse.label,
      unitName: selectedUnit?.label || '',
      lessonGuid: selectedLesson.value,
      contentIds: selectedLesson.data?.contents?.map((c) => c.id) || [],
      type: 'lesson',
    };

    onSelect(lesson);
  }, [selectedLesson, selectedCourse, selectedUnit, hasLessons, onSelect]);

  const toggleUnit = useCallback((unitId) => {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  }, []);

  const toggleLesson = useCallback((lesson) => {
    setSelectedLessonGuids((prev) => toggleLessonSelection(prev, lesson));
  }, []);

  const toggleUnitLessons = useCallback((unit) => {
    setSelectedLessonGuids((prev) => toggleUnitSelection(prev, unit));
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedLessonGuids(new Set());
  }, []);

  const isConfirmDisabled = hasLessons ? !selectedLesson || !selectedCourse || !selectedUnit : !selectedCourse || !selectedUnit;

  const handleConfirm = useCallback(() => {
    const lessons = [];
    (units || []).forEach((unit) => {
      if (isModulesOnlyTocUnit(unit)) {
        // Modules-only: the unit itself is the selectable item
        if (effectiveSelectedIds.has(unit.id)) {
          lessons.push({
            guid: unit.id,
            title: decodeHtmlWithWhiteList(unit.title || ''),
            wordCount: null,
            courseGuid: selectedCourse?.value,
            courseName: selectedCourse?.label,
            unitName: decodeHtmlWithWhiteList(unit.title || ''),
            lessonGuid: unit.id,
            contentIds: unit.contents?.map((c) => c.id) || [],
            type: 'module',
          });
        }
      } else {
        (unit.lessons || []).forEach((lesson) => {
          if (effectiveSelectedIds.has(lesson.id)) {
            lessons.push({
              guid: lesson.id,
              title: decodeHtmlWithWhiteList(lesson.title || ''),
              wordCount: null,
              courseGuid: selectedCourse?.value,
              courseName: selectedCourse?.label,
              unitName: decodeHtmlWithWhiteList(unit.title || ''),
              lessonGuid: lesson.id,
              contentIds: lesson.contents?.map((c) => c.id) || [],
              type: 'lesson',
            });
          }
        });
      }
    });

    onSelect(lessons);
  }, [effectiveSelectedIds, units, selectedCourse, onSelect]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  if (!multiSelect) {
    // Single-select mode: 3 cascading dropdowns
    return (
      <Dialog
        isOpen={open}
        onClose={onClose}
        onSuccess={handleSingleConfirm}
        title={t('chatbot:Choose content from program')}
        buttonTextSuccess={t('actions:Send')}
        isDisabledSuccess={isConfirmDisabled}
        size="l"
        className="lesson-selection-dialog"
      >
        <div className="lesson-selection-dialog__content">
          <div className="lesson-selection-dialog__section">
            <label className="lesson-selection-dialog__section-label">{t('chatbot:Subject')}</label>
            <SelectCustomizable
              options={courseOptions}
              value={selectedCourse}
              onChange={handleCourseChange}
              placeholder={t('chatbot:Select subject')}
              noOptionsMessage={t('chatbot:No options')}
              isSearchable={true}
              isClearable={false}
              isLoading={loadingCourses}
            />
          </div>

          <div className="lesson-selection-dialog__section">
            <label className="lesson-selection-dialog__section-label">{t('chatbot:Unit')}</label>
            <SelectCustomizable
              options={unitOptions}
              value={selectedUnit}
              onChange={handleUnitChange}
              placeholder={t('chatbot:Select unit')}
              noOptionsMessage={t('chatbot:No options')}
              isSearchable={true}
              isClearable={false}
              isLoading={loadingUnits}
              isDisabled={!selectedCourse}
            />
          </div>

          {hasLessons && (
            <div className="lesson-selection-dialog__section">
              <label className="lesson-selection-dialog__section-label">{t('chatbot:Lesson')}</label>
              <SelectCustomizable
                options={lessonOptions}
                value={selectedLesson}
                onChange={handleLessonChange}
                placeholder={t('chatbot:Select lesson')}
                noOptionsMessage={t('chatbot:No options')}
                isSearchable={true}
                isClearable={false}
                isDisabled={!selectedUnit}
              />
              {selectedLesson && (
                <div className="lesson-selection-dialog__link">
                  <a
                    href={`/course/${selectedCourse?.value}/lesson/${selectedLesson?.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lesson-selection-dialog__link-text"
                  >
                    {t('chatbot:see_book_content')}
                  </a>
                </div>
              )}
            </div>
          )}

          {contentError && (
            <div className="lesson-selection-dialog__error">
              <div className="lesson-selection-dialog__error-message">{t('chatbot:teacher_tools_error_content_unavailable_message')}</div>
            </div>
          )}
        </div>
      </Dialog>
    );
  }

  function getCheckboxClassName(checkState, isDisabled) {
    return clsx(
      'lesson-selection-dialog__checkbox',
      `lesson-selection-dialog__checkbox--${checkState}`,
      isDisabled && 'lesson-selection-dialog__checkbox--disabled'
    );
  }

  function renderCheckMark(checkState) {
    if (checkState === 'checked') return <Icon color="white" name="check" />;
    if (checkState === 'indeterminate') return <span className="lesson-selection-dialog__checkbox-indeterminate" />;
    return null;
  }

  function renderLesson(lessonNode, lessonIndex) {
    const { describedById, isDisabled, isSelected, lesson } = lessonNode;
    const checkState = isSelected ? 'checked' : 'unchecked';
    const lessonTitle = decodeHtmlWithWhiteList(lesson.title || `${t('chatbot:Lesson')} ${lessonIndex + 1}`);

    function handleLessonCheckboxClick() {
      toggleLesson(lesson);
    }

    return (
      <li
        key={lesson.id}
        className={clsx(
          'lesson-selection-dialog__lesson-row',
          isSelected && 'lesson-selection-dialog__lesson-row--selected',
          isDisabled && 'lesson-selection-dialog__lesson-row--disabled'
        )}
      >
        <button
          aria-checked={isSelected}
          aria-describedby={describedById}
          aria-disabled={isDisabled}
          className="lesson-selection-dialog__option lesson-selection-dialog__option--lesson"
          onClick={handleLessonCheckboxClick}
          role="checkbox"
          type="button"
        >
          <span className={getCheckboxClassName(checkState, isDisabled)}>{renderCheckMark(checkState)}</span>
          <span className="lesson-selection-dialog__lesson-name">{lessonTitle}</span>
        </button>
        {/* Tag takes no id, so the aria-describedby target is a span inside its label. */}
        {isDisabled && (
          <Tag
            className="lesson-selection-dialog__lesson-tag"
            color="pastel-grey"
            label={<span id={describedById}>{t('chatbot:lesson_selection_no_ai_tag')}</span>}
          />
        )}
      </li>
    );
  }

  function renderUnitCounter({ contentsCount, hasNoAvailableLessons, isModulesOnly, selectedCount, totalCount }) {
    if (isModulesOnly) return `${contentsCount} ${t('chatbot:contents')}`;
    if (hasNoAvailableLessons) return t('chatbot:lesson_selection_unit_no_ai');
    return `${selectedCount} ${t('chatbot:of_lessons', { total: totalCount })}`;
  }

  function renderUnit(unitNode, unitIndex) {
    const { checkState, describedById, isDisabled, isExpandable, isModulesOnly, lessons, noAiCounterId, unit } = unitNode;
    const isExpanded = isExpandable && expandedUnits.has(unit.id);
    const unitRowClassName = clsx('lesson-selection-dialog__unit-row', isDisabled && !isExpandable && 'lesson-selection-dialog__unit-row--inert');
    const unitTitle = decodeHtmlWithWhiteList(unit.title || `${t('chatbot:Unit')} ${unitIndex + 1}`);

    function handleUnitCheckboxClick() {
      toggleUnitLessons(unit);
    }

    function handleUnitToggleClick() {
      toggleUnit(unit.id);
    }

    const unitInfo = (
      <span className="lesson-selection-dialog__unit-info">
        <span className="lesson-selection-dialog__unit-name">{unitTitle}</span>
        <span className="lesson-selection-dialog__unit-counter" id={noAiCounterId}>
          {renderUnitCounter(unitNode)}
        </span>
      </span>
    );

    // A modules-only unit has nothing to expand, so its title and counter are part of its checkbox.
    if (isModulesOnly) {
      return (
        <li key={unit.id} className="lesson-selection-dialog__unit">
          <div className={unitRowClassName}>
            <button
              aria-checked={ARIA_CHECKED_BY_STATE[checkState]}
              aria-describedby={describedById}
              aria-disabled={isDisabled}
              className="lesson-selection-dialog__option lesson-selection-dialog__option--unit"
              onClick={handleUnitCheckboxClick}
              role="checkbox"
              type="button"
            >
              <span className={getCheckboxClassName(checkState, isDisabled)}>{renderCheckMark(checkState)}</span>
              {unitInfo}
            </button>
          </div>
        </li>
      );
    }

    return (
      <li key={unit.id} className="lesson-selection-dialog__unit">
        <div className={unitRowClassName}>
          <button
            aria-checked={ARIA_CHECKED_BY_STATE[checkState]}
            aria-describedby={describedById}
            aria-disabled={isDisabled}
            aria-label={unitTitle}
            className={getCheckboxClassName(checkState, isDisabled)}
            onClick={handleUnitCheckboxClick}
            role="checkbox"
            type="button"
          >
            {renderCheckMark(checkState)}
          </button>
          {isExpandable ? (
            <button aria-expanded={isExpanded} className="lesson-selection-dialog__unit-toggle" onClick={handleUnitToggleClick} type="button">
              {unitInfo}
              <Icon className="lesson-selection-dialog__unit-chevron" name={isExpanded ? 'chevron-up' : 'chevron-down'} size="lg" />
            </button>
          ) : (
            unitInfo
          )}
        </div>
        {isExpanded && <ul className="lesson-selection-dialog__lessons">{lessons.map(renderLesson)}</ul>}
      </li>
    );
  }

  // Multi-select mode: tree with checkboxes
  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      onSuccess={handleConfirm}
      title={t('chatbot:Choose content from program')}
      buttonTextSuccess={t('chatbot:Select content')}
      isDisabledSuccess={totalSelectedCount === 0}
      size="l"
      className="lesson-selection-dialog"
      customFooterLeft={
        <span className="lesson-selection-dialog__footer-count">
          {totalSelectedCount === 1
            ? t('chatbot:lessons_chosen', { count: totalSelectedCount })
            : t('chatbot:lessons_chosen_plural', { count: totalSelectedCount })}
        </span>
      }
    >
      <div className="lesson-selection-dialog__content">
        {/* ASIGNATURA section */}
        <div className="lesson-selection-dialog__section">
          <label className="lesson-selection-dialog__section-label">{t('chatbot:Subject')}</label>
          <SelectCustomizable
            options={courseOptions}
            value={selectedCourse}
            onChange={handleCourseChange}
            placeholder={t('chatbot:Select subject')}
            noOptionsMessage={t('chatbot:No options')}
            isSearchable={true}
            isClearable={false}
            isLoading={loadingCourses}
          />
        </div>

        {/* SELECCIÓN DE TEMARIO section - always visible per Figma spec */}
        <div className="lesson-selection-dialog__section">
          <label className="lesson-selection-dialog__section-label lesson-selection-dialog__section-label--with-help">{t('chatbot:Syllabus selection')}</label>
          <p className="lesson-selection-dialog__help" id={UNAVAILABLE_HELP_ID}>{t('chatbot:lesson_selection_unavailable_help')}</p>

          {/* Search input */}
          <div className="lesson-selection-dialog__search">
            <input
              type="text"
              className="lesson-selection-dialog__search-input"
              placeholder={t('chatbot:Search unit or lesson')}
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={!selectedCourse}
            />
            {searchQuery ? (
              <Button className="lesson-selection-dialog__search-icon" variant="plain" isIconOnly leftIcon="close" ariaLabel={t('chatbot:Close')} onClick={handleClearSearch} />
            ) : (
              <span className="lesson-selection-dialog__search-icon">
                <Icon name="search" />
              </span>
            )}
          </div>

          {/* Unit/Lesson tree */}
          <div className="lesson-selection-dialog__tree">
            {!selectedCourse ? (
              <div className="lesson-selection-dialog__empty">{t('chatbot:Select subject')}</div>
            ) : loadingUnits ? (
              <div className="lesson-selection-dialog__loading">{t('chatbot:Loading...')}</div>
            ) : filteredUnits.length === 0 ? (
              <div className="lesson-selection-dialog__empty">{searchQuery ? t('chatbot:No search results') : t('chatbot:No units available')}</div>
            ) : (
              <ul className="lesson-selection-dialog__units">{lessonTree.map(renderUnit)}</ul>
            )}
          </div>

          {/* Select all / Clear actions */}
          <div className="lesson-selection-dialog__actions">
            <div>
              <Button
                text={t('chatbot:Clear selection')}
                size="xs"
                variant="primary-ghost"
                onClick={handleClearAll}
                isDisabled={!selectedCourse || totalSelectedCount === 0}
                isFullWidth
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

LessonSelectionDialog.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  courses: PropTypes.arrayOf(PropTypes.object).isRequired,
  units: PropTypes.arrayOf(PropTypes.object),
  loadingUnits: PropTypes.bool,
  loadCourseUnits: PropTypes.func.isRequired,
  loadingCourses: PropTypes.bool,
  contentError: PropTypes.string,
  alreadySelectedLessons: PropTypes.arrayOf(PropTypes.object),
  multiSelect: PropTypes.bool,
  defaultCourseId: PropTypes.string,
};

export default withTranslation(['chatbot', 'actions'])(LessonSelectionDialog);
